import { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  signOut,
  onAuthStateChanged,
  UserCredential,
  GoogleAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import axios from "axios";
import { auth } from "./firebase";
import { api } from "./api";

interface DbUser {
  _id: string;
  email: string;
  roles: string[];
  isActive: boolean;
}

/**
 * Extended User type with additional fields from our database
 */
interface ExtendedUser extends User {
  roles?: string[];
  isActive?: boolean;
}

/**
 * Interface for the authentication context value
 */
interface AuthContextValue {
  user: ExtendedUser | null;
  loading: boolean;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithGithub: () => Promise<UserCredential>;
  logout: () => Promise<void>;
}

/**
 * Create the authentication context
 */
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provider component for authentication context
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data from our API
  const fetchUserData = async (firebaseUser: User) => {
    try {
      // Ensure we have a fresh token before making the request
      const token = await firebaseUser.getIdToken(true);
      console.log("Token refresh status:", {
        tokenLength: token.length,
        metadata: firebaseUser.metadata,
      });
      const response = await api.get<DbUser>(`/users/${firebaseUser.uid}`);
      const dbUser = response.data;

      // Extend Firebase user with our database fields
      const extendedUser: ExtendedUser = Object.assign(firebaseUser, {
        roles: dbUser.roles,
        isActive: dbUser.isActive,
      });

      setUser(extendedUser);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.error("Error fetching user data:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          headers: error.config?.headers,
          responseData: error.response?.data,
        });
      } else {
        console.error("Error fetching user data:", error);
      }

      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // If unauthorized, try to refresh token one more time
        console.log("Attempting to refresh token and retry request...");
        try {
          const retryToken = await firebaseUser.getIdToken(true);
          console.log("Retry token refresh status:", {
            tokenLength: retryToken.length,
            metadata: firebaseUser.metadata,
          });
          const retryResponse = await api.get<DbUser>(
            `/users/${firebaseUser.uid}`,
          );
          const dbUser = retryResponse.data;
          const extendedUser: ExtendedUser = Object.assign(firebaseUser, {
            roles: dbUser.roles,
            isActive: dbUser.isActive,
          });
          setUser(extendedUser);
          return;
        } catch (retryError) {
          if (axios.isAxiosError(retryError)) {
            console.error("Error retrying user data fetch:", {
              status: retryError.response?.status,
              statusText: retryError.response?.statusText,
              url: retryError.config?.url,
              headers: retryError.config?.headers,
              responseData: retryError.response?.data,
            });
          } else {
            console.error("Error retrying user data fetch:", retryError);
          }
        }
      }
      // If we can't fetch user data, still set the Firebase user
      setUser(firebaseUser);
    }
  };

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: User | null) => {
        if (firebaseUser) {
          // User is signed in, fetch additional data
          await fetchUserData(firebaseUser);
        } else {
          // User is signed out
          setUser(null);
        }
        setLoading(false);
      },
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  /**
   * Sign out the current user
   */
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  /**
   * Sign in with Google using popup
   */
  const signInWithGoogle = async (): Promise<UserCredential> => {
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(auth, provider);

      // Check if user exists in our database, if not create them
      try {
        await api.get(`/users/${credential.user.uid}`);
      } catch (error) {
        // User doesn't exist, create them
        await api.post("/users", {
          _id: credential.user.uid,
          email: credential.user.email,
          roles: ["user"],
          isActive: false,
        });
      }

      await fetchUserData(credential.user);
      return credential;
    } catch (error) {
      console.error("Error signing in with Google:", error);
      throw error;
    }
  };

  /**
   * Sign in with GitHub using popup
   */
  const signInWithGithub = async (): Promise<UserCredential> => {
    try {
      const provider = new GithubAuthProvider();
      const credential = await signInWithPopup(auth, provider);

      // Check if user exists in our database, if not create them
      try {
        await api.get(`/users/${credential.user.uid}`);
      } catch (error) {
        // User doesn't exist, create them
        await api.post("/users", {
          _id: credential.user.uid,
          email: credential.user.email,
          roles: ["user"],
          isActive: true,
        });
      }

      await fetchUserData(credential.user);
      return credential;
    } catch (error) {
      console.error("Error signing in with GitHub:", error);
      throw error;
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    signInWithGoogle,
    signInWithGithub,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook to use the auth context
 * @throws {Error} If used outside of AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
